/** Actual navigation registry. Labels do not assert backend readiness or permission. */
export interface Screen {
  id: string;
  path: string;
  title: string;
  module: string;
  description: string;
  kind: string;
  icon: string;
}
export interface Module {
  id: string;
  label: string;
  icon: string;
  screens: Screen[];
}

/** Public entry points are registered routes, not signed-in navigation screens. */
export const publicEntryRoutes = Object.freeze({
  firmSignup: "/auth/signup",
  portalInvitation: "/portal/invitation",
});

// prettier-ignore
const navigationData =
  // NAVIGATION_DATA_BEGIN
[
  {
    "id": "workspace",
    "label": "Workspace",
    "icon": "LayoutDashboard",
    "screens": [
      [
        "dashboard",
        "Overview",
        "dashboard",
        "Your returns, open tasks and next preparation steps."
      ],
      [
        "cases",
        "Returns",
        "cases",
        "Create, search and manage local draft return records."
      ],
      [
        "assignments",
        "Assignments",
        "assignments",
        "Review ownership, preparation responsibilities and independent assignments."
      ],
      [
        "activity",
        "Activity",
        "activity",
        "Trace recorded workspace activity without inventing execution evidence."
      ],
      [
        "timeline",
        "Return Timeline",
        "activity",
        "Follow return history, changes and workflow milestones."
      ],
      [
        "notifications",
        "Notifications",
        "notifications",
        "Manage attention, messages and outstanding work."
      ]
    ]
  },
  {
    "id": "intake",
    "label": "Intake",
    "icon": "ClipboardList",
    "screens": [
      [
        "new",
        "Create Return",
        "case-create",
        "Start a synthetic tax return and gather the information you need."
      ],
      [
        "questionnaire",
        "Return Builder",
        "questionnaire",
        "Build the return from taxpayer details and documents through federal return review."
      ],
      [
        "facts",
        "Personal Information",
        "facts",
        "Enter personal details and information used to prepare the return."
      ],
      [
        "supplemental",
        "Personal & filing details",
        "supplemental-facts",
        "Add contact information, mailing details and available filing preferences."
      ],
      [
        "evidence-upload",
        "Upload documents",
        "upload",
        "Keep the taxpayer’s original documents together with their return."
      ],
      [
        "w2-upload",
        "W-2 forms",
        "upload",
        "Add each employer’s W-2, including corrected forms and supporting statements."
      ],
      [
        "evidence-review",
        "Review documents",
        "evidence",
        "Check the documents provided and identify anything missing or inconsistent."
      ],
      [
        "reconciliation",
        "Compare information",
        "reconciliation",
        "Review saved answers alongside the taxpayer’s original documents."
      ],
      [
        "admission",
        "Intake checklist",
        "admission",
        "See what has been saved and what still needs attention before preparation can continue."
      ]
    ]
  },
  {
    "id": "evidence",
    "label": "Evidence",
    "icon": "Files",
    "screens": [
      [
        "viewer",
        "Evidence viewer",
        "evidence",
        "Inspect documents, provenance and source metadata."
      ],
      [
        "timeline",
        "Evidence timeline",
        "activity",
        "Trace acquisition, selection and review history."
      ],
      [
        "lineage",
        "Evidence lineage",
        "lineage",
        "Connect selected facts to original evidence and required proofs."
      ],
      [
        "sufficiency",
        "Evidence sufficiency",
        "coverage",
        "Inspect the exact required evidence minima and missing support."
      ],
      [
        "comparison",
        "Evidence comparison",
        "reconciliation",
        "Compare selected records and retained source versions."
      ],
      [
        "duplicates",
        "Duplicate analysis",
        "evidence",
        "Identify duplicate source records without silently deleting history."
      ],
      [
        "conflicts",
        "Conflict resolution",
        "findings",
        "Record competing assertions and required resolution evidence."
      ]
    ]
  },
  {
    "id": "rules",
    "label": "Rule graph",
    "icon": "Network",
    "screens": [
      [
        "explorer",
        "Rule explorer",
        "rules",
        "Explore exact primary rules, tax families and authority dependencies."
      ],
      [
        "dependencies",
        "Dependencies",
        "graph",
        "Follow the imported directed rule dependency graph."
      ],
      [
        "nodes",
        "Node browser",
        "rules",
        "Inspect primary and auxiliary atomic rule contracts."
      ],
      [
        "authorities",
        "Authority viewer",
        "authorities",
        "Inspect IRS authority sources and unresolved acquisition or review status."
      ],
      [
        "trace",
        "Graph trace",
        "lineage",
        "Follow rule inputs, outputs and original source bindings."
      ],
      [
        "diagnostics",
        "Graph diagnostics",
        "diagnostics",
        "Inspect closure requirements and source-defined diagnostic contracts."
      ]
    ]
  },
  {
    "id": "compiler",
    "label": "Compiler",
    "icon": "Workflow",
    "screens": [
      [
        "dashboard",
        "Compiler overview",
        "compiler",
        "Inspect compilation contracts, source inputs and backend availability."
      ],
      [
        "imports",
        "Import jobs",
        "operations",
        "Inspect import operation contracts and local job records."
      ],
      [
        "normalization",
        "Normalization",
        "compiler",
        "Inspect canonicalization and typed source normalization requirements."
      ],
      [
        "symbols",
        "Symbol resolution",
        "compiler",
        "Browse original symbol and result-reference contracts."
      ],
      [
        "dependencies",
        "Dependency graph",
        "graph",
        "Inspect source-defined producer and consumer edges."
      ],
      [
        "plan",
        "Execution plan",
        "compiler",
        "Inspect the frozen graph plan definition without claiming an executed compiler."
      ],
      [
        "diagnostics",
        "Compiler diagnostics",
        "diagnostics",
        "Inspect compiler errors and required validation conditions."
      ]
    ]
  },
  {
    "id": "evaluator",
    "label": "Evaluator",
    "icon": "Play",
    "screens": [
      [
        "console",
        "Execution console",
        "operations",
        "Inspect evaluator integration and explicit runtime availability."
      ],
      [
        "sessions",
        "Runtime sessions",
        "operations",
        "Inspect actual local session records and required upstream contracts."
      ],
      [
        "facts",
        "Runtime facts",
        "facts",
        "Inspect typed input fact contracts and ownership boundaries."
      ],
      [
        "results",
        "Results",
        "results",
        "Inspect result bindings; no calculated values are fabricated."
      ],
      [
        "diagnostics",
        "Diagnostics",
        "diagnostics",
        "Inspect evaluation findings and exact source diagnostic identities."
      ],
      [
        "replay",
        "Replay",
        "replay",
        "Inspect deterministic replay prerequisites and original artifact bindings."
      ],
      [
        "lineage",
        "Result lineage",
        "lineage",
        "Trace required source-to-result proof relationships."
      ]
    ]
  },
  {
    "id": "validation",
    "label": "Validation",
    "icon": "ShieldCheck",
    "screens": [
      [
        "dashboard",
        "Validation overview",
        "validation",
        "Inspect validation coverage, open work and verification readiness."
      ],
      [
        "reconciliation",
        "Reconciliation",
        "reconciliation",
        "Inspect fact, document, calculation and form reconciliation obligations."
      ],
      [
        "coverage",
        "Coverage",
        "coverage",
        "Explore source-backed rule, field and test catalog coverage."
      ],
      [
        "evidence",
        "Evidence sufficiency",
        "coverage",
        "Inspect evidence requirements without inferring support from uploads alone."
      ],
      [
        "findings",
        "Open findings",
        "findings",
        "Create and manage local findings with explicit source references."
      ],
      [
        "final-checks",
        "Final checks",
        "gates",
        "Inspect final validation gates and required evidence."
      ]
    ]
  },
  {
    "id": "review",
    "label": "Review",
    "icon": "UserCheck",
    "screens": [
      [
        "preparer",
        "Preparer workspace",
        "review",
        "Organize preparation work and exact review responsibilities."
      ],
      [
        "reviewer",
        "Reviewer workspace",
        "review",
        "Inspect return evidence and independent review requirements."
      ],
      [
        "independent",
        "Independent review",
        "review",
        "Inspect separation of duties and independently required proof."
      ],
      [
        "checklists",
        "Review checklists",
        "checklists",
        "Work through the original reviewed-basis and evidence checklists."
      ],
      [
        "findings",
        "Review findings",
        "findings",
        "Track local review findings without manufacturing signed dispositions."
      ],
      [
        "decisions",
        "Review decisions",
        "decisions",
        "Inspect decision contracts; actual signed approvals require the governed backend."
      ]
    ]
  },
  {
    "id": "forms",
    "label": "Form preparation",
    "icon": "FileText",
    "screens": [
      [
        "1040",
        "Form 1040",
        "form",
        "Review the completed return when preparation becomes available. No return has been generated yet."
      ],
      [
        "fields",
        "Field mappings",
        "fields",
        "Trace every logical Form 1040 field to its exact source/result mapping."
      ],
      [
        "explanations",
        "Explanations",
        "explanations",
        "Inspect authority, rule and source requirements for each explanation."
      ],
      [
        "trace",
        "Source trace",
        "lineage",
        "Connect form dispositions with required facts, rules and evidence."
      ],
      [
        "package",
        "Preparation package",
        "package",
        "Inspect artifact membership and original reproducibility requirements."
      ]
    ]
  },
  {
    "id": "certification",
    "label": "Certification",
    "icon": "BadgeCheck",
    "screens": [
      [
        "oracles",
        "Oracle management",
        "oracles",
        "Inspect independently required expectations and pre-observation commitments."
      ],
      [
        "golden-cases",
        "Golden Returns",
        "golden",
        "Inspect synthetic return definitions without claiming executed outcomes."
      ],
      [
        "mutations",
        "Mutation testing",
        "tests",
        "Browse original mutation-test definitions and required observations."
      ],
      [
        "replay",
        "Replay certification",
        "replay",
        "Inspect the exact fresh and restored-environment replay obligations."
      ],
      [
        "coverage",
        "Coverage certification",
        "gates",
        "Inspect certification gate requirements and exact coverage sets."
      ],
      [
        "decisions",
        "Certification decisions",
        "decisions",
        "Inspect internal decision contracts and truthful certification state."
      ]
    ]
  },
  {
    "id": "releases",
    "label": "Releases",
    "icon": "Package",
    "screens": [
      [
        "assembly",
        "Release assembly",
        "releases",
        "Inspect immutable release content and detached governance bindings."
      ],
      [
        "verification",
        "Release verification",
        "gates",
        "Inspect integrity, source freshness and exact release gates."
      ],
      [
        "approval",
        "Release approval",
        "approvals",
        "Inspect required independent approval roles and signed decision prerequisites."
      ],
      [
        "qualification",
        "Release qualification",
        "gates",
        "Inspect technical qualification requirements without inventing a qualified release."
      ],
      [
        "deployment",
        "Deployment readiness",
        "environments",
        "Inspect deployment profiles and explicitly unmet runtime prerequisites."
      ],
      [
        "current-use",
        "Current use",
        "releases",
        "Inspect current-use contracts; historical hashes do not authorize execution."
      ]
    ]
  },
  {
    "id": "audit",
    "label": "Audit",
    "icon": "History",
    "screens": [
      [
        "explorer",
        "Audit explorer",
        "audit",
        "Search the local append-only application event history."
      ],
      [
        "replay",
        "Historical replay",
        "replay",
        "Inspect historical reproduction requirements without executing a tax return."
      ],
      [
        "manifests",
        "Manifest browser",
        "manifests",
        "Browse source manifests and exact immutable membership definitions."
      ],
      [
        "evidence",
        "Evidence browser",
        "evidence",
        "Inspect original artifact provenance and source metadata."
      ],
      [
        "certification",
        "Certification history",
        "audit",
        "Inspect recorded certification evidence; absent history remains empty."
      ],
      [
        "releases",
        "Release history",
        "audit",
        "Inspect release evidence and authentic historical states."
      ]
    ]
  },
  {
    "id": "admin",
    "label": "Administration",
    "icon": "Settings2",
    "screens": [
      [
        "users",
        "Users",
        "users",
        "Inspect local workspace profiles and identity-provider connection state."
      ],
      [
        "roles",
        "Roles",
        "roles",
        "Inspect original human, workload and assignment role definitions."
      ],
      [
        "permissions",
        "Permissions",
        "permissions",
        "Inspect exact source-defined operation permissions and default-deny conditions."
      ],
      [
        "tenants",
        "Tenants",
        "tenants",
        "Inspect workspace isolation and tenant context."
      ],
      [
        "datasets",
        "Datasets",
        "datasets",
        "Browse source-defined tax tables, scalars and binding requirements."
      ],
      [
        "authorities",
        "Authorities",
        "authorities",
        "Inspect authoritative source inventory and explicit nonfrozen status."
      ],
      [
        "schemas",
        "Schemas",
        "schemas",
        "Browse exact registered Draft 2020-12 source schemas."
      ],
      [
        "policies",
        "Policies",
        "policies",
        "Inspect scope, rounding, signing and integrity policies."
      ],
      [
        "flags",
        "Feature flags",
        "flags",
        "Manage local interface preferences without enabling excluded tax functionality."
      ],
      [
        "environments",
        "Environments",
        "environments",
        "Inspect environment boundaries and integration availability."
      ]
    ]
  },
  {
    "id": "auth",
    "label": "Authentication",
    "icon": "LockKeyhole",
    "screens": [
      [
        "login",
        "Login",
        "login",
        "Access the workspace through the configured identity connection."
      ],
      [
        "password-reset",
        "Password reset",
        "password-reset",
        "Inspect account-recovery connection state without collecting production secrets."
      ],
      [
        "mfa",
        "Multi-factor authentication",
        "mfa",
        "Inspect MFA enrollment and identity-provider requirements."
      ],
      [
        "sessions",
        "Session management",
        "sessions",
        "Inspect the current application session and session controls."
      ],
      [
        "devices",
        "Security devices",
        "devices",
        "Inspect registered-device connection state and local session context."
      ]
    ]
  },
  {
    "id": "productivity",
    "label": "Work center",
    "icon": "ListTodo",
    "screens": [
      [
        "tasks",
        "Task center",
        "tasks",
        "Create, assign, filter and resolve local work items."
      ],
      [
        "approvals",
        "Approval center",
        "approvals",
        "Inspect pending governed actions without granting simulated legal approval."
      ],
      [
        "saved-views",
        "Saved views",
        "settings",
        "Manage saved table configurations and workspace filters."
      ],
      [
        "pinned",
        "Pinned records",
        "settings",
        "Return to pinned source and workspace records."
      ]
    ]
  },
  {
    "id": "settings",
    "label": "Settings",
    "icon": "SlidersHorizontal",
    "screens": [
      [
        "preferences",
        "Preferences",
        "settings",
        "Manage accessibility, reduced motion and workspace display preferences."
      ],
      [
        "design-system",
        "Design system",
        "design-system",
        "Inspect the live typography, emerald palette, components and interaction states."
      ],
      [
        "integrations",
        "Integrations",
        "settings",
        "Inspect real API connection configuration and unavailable integration boundaries."
      ]
    ]
  }
] as const;
// NAVIGATION_DATA_END

export const modules: Module[] = navigationData.map((module) => ({
  id: module.id,
  label: module.label,
  icon: module.icon,
  screens: module.screens.map(([key, title, kind, description]) => ({
    id: `${module.id}.${key}`,
    path: `/${module.id}/${key}`,
    title,
    module: module.id,
    description,
    kind,
    icon: module.icon,
  })),
}));
export const screens: Screen[] = modules.flatMap((module) => module.screens);
